import { useId, useState } from "react";
import { Link } from "react-router";

import { BillingContactPanel } from "./BillingContactPanel.tsx";
import { TRANSACTIONS_COPY } from "./transactions.constants.ts";
import { useTransactionsController } from "./useTransactionsController.ts";
import type { FeeRow } from "./transactions.model.ts";
import "./gallery.css";
import "./transactions.css";

/**
 * The Transaction Centre — what a sale produces, in one place.
 *
 * Three counts across the top, each a link to the screen that owns the act,
 * and the fee ledger beneath them, which has no other screen. The fees are
 * grouped the way the settlements screen groups its rows: what is due first,
 * then what was recorded as paid, then what a withdrawal voided — filed apart
 * from the paid, so a withdrawn sale can never read as a paid one.
 *
 * IT READS THE TABLE, NEVER A FEED. What is owed is a "what is true" question.
 *
 * `canRecord` is the admin rung. Recording a fee as paid commits the firm, so a
 * cataloguer sees the ledger and a sentence where a director sees the control.
 *
 * It carries no title of its own: the portal header above it does.
 */
export function TransactionCentre({
  workBase,
  soldHref,
  settlementsHref,
  canRecord,
  firmOndoId,
}: {
  workBase: string;
  soldHref: string;
  settlementsHref: string;
  canRecord: boolean;
  /**
   * The firm whose ledger this is — the organisation's ONDO id, or the person's
   * where somebody sells with no firm behind them. Required rather than
   * optional, because a billing contact belongs to exactly one principal.
   */
  firmOndoId: string;
}) {
  const {
    view,
    billing,
    changingBilling,
    confirmingId,
    working,
    askToRecord,
    cancelRecord,
    record,
    chooseBillingContact,
  } = useTransactionsController(workBase, firmOndoId);

  if (view.kind === "loading") {
    return (
      <p className="gallery__note" role="status">
        {view.message}
      </p>
    );
  }

  if (view.kind === "error") {
    return (
      <section className="gallery__note gallery__note--error" role="alert">
        <h2 className="gallery__note-title">{view.title}</h2>
        <p>{view.message}</p>
      </section>
    );
  }

  return (
    <>
      <section className="gallery__lead">
        <p>
          <strong>{TRANSACTIONS_COPY.lead}</strong> {TRANSACTIONS_COPY.leadDetail}
        </p>
      </section>

      <ul className="transactions__tiles">
        <Tile
          count={view.tiles.awaitingBuyer}
          label={TRANSACTIONS_COPY.tiles.awaitingBuyer}
          note={TRANSACTIONS_COPY.tiles.awaitingBuyerNote}
          href={soldHref}
        />
        <Tile
          count={view.tiles.feesDue}
          label={TRANSACTIONS_COPY.tiles.feesDue}
          note={view.feesDueSummary || TRANSACTIONS_COPY.tiles.feesDueNote}
          href={null}
        />
        <Tile
          count={view.tiles.settlementsDue}
          label={TRANSACTIONS_COPY.tiles.settlementsDue}
          note={TRANSACTIONS_COPY.tiles.settlementsDueNote}
          href={settlementsHref}
        />
      </ul>

      <BillingContactPanel
        billing={billing}
        canChoose={canRecord}
        changing={changingBilling}
        onChoose={chooseBillingContact}
      />

      {view.empty && <p className="gallery__note">{view.empty}</p>}

      {view.due.length > 0 && (
        <Group title={TRANSACTIONS_COPY.groups.due} count={view.due.length}>
          {view.due.map((row) => (
            <FeeCard
              key={row.feeId}
              row={row}
              action={
                canRecord ? (
                  <RecordControl
                    confirming={confirmingId === row.feeId}
                    busy={working === row.feeId}
                    onAsk={() => askToRecord(row.feeId)}
                    onCancel={cancelRecord}
                    onRecord={(note) => record(row.feeId, note)}
                  />
                ) : (
                  <p className="gallery__note gallery__note--quiet">
                    {TRANSACTIONS_COPY.needsAdmin}
                  </p>
                )
              }
            />
          ))}
        </Group>
      )}

      {view.paid.length > 0 && (
        <Group title={TRANSACTIONS_COPY.groups.paid} count={view.paid.length}>
          {view.paid.map((row) => (
            <FeeCard key={row.feeId} row={row} action={null} />
          ))}
        </Group>
      )}

      {view.void.length > 0 && (
        <Group title={TRANSACTIONS_COPY.groups.void} count={view.void.length}>
          {view.void.map((row) => (
            <FeeCard key={row.feeId} row={row} action={null} />
          ))}
        </Group>
      )}
    </>
  );
}

function Tile({
  count,
  label,
  note,
  href,
}: {
  count: number;
  label: string;
  note: string;
  href: string | null;
}) {
  const body = (
    <>
      <span className="transactions__tile-count">{count}</span>
      <span className="transactions__tile-label">{label}</span>
      <span className="transactions__tile-note">{note}</span>
      {href && (
        <span className="transactions__tile-open" aria-hidden="true">
          {TRANSACTIONS_COPY.tiles.open} →
        </span>
      )}
    </>
  );

  return (
    <li className="transactions__tile">
      {href ? (
        <Link className="transactions__tile-link" to={href}>
          {body}
        </Link>
      ) : (
        <div className="transactions__tile-link transactions__tile-link--here">{body}</div>
      )}
    </li>
  );
}

function Group({
  title,
  count,
  children,
}: {
  title: string;
  count: number;
  children: React.ReactNode;
}) {
  return (
    <section>
      <div className="gallery__group">
        <h2 className="gallery__group-title">
          {title} ({count})
        </h2>
      </div>
      <ul className="transactions__fees">{children}</ul>
    </section>
  );
}

function FeeCard({ row, action }: { row: FeeRow; action: React.ReactNode }) {
  return (
    <li className={`transactions__fee transactions__fee--${row.state}`}>
      <Link className="transactions__fee-open" to={row.href}>
        <div className="transactions__fee-body">
          <p className="transactions__fee-title">
            {row.title}
            <span className={`transactions__fee-state transactions__fee-state--${row.state}`}>
              {row.stateLabel}
            </span>
          </p>
          <p className="transactions__fee-meta">{row.identity}</p>
          <p className="transactions__fee-meta">
            {row.sale} · {row.tierLabel}
          </p>
        </div>
        <div className="transactions__fee-amount">
          <span className="transactions__fee-amount-value">{row.amount}</span>
        </div>
      </Link>
      {(row.note || action) && (
        <div className="transactions__fee-foot">
          {row.note && <p className="transactions__fee-note">{row.note}</p>}
          {action}
        </div>
      )}
    </li>
  );
}

/**
 * Two presses, with a reference between them.
 *
 * The armed state REPLACES the button, so a row can never show "Record as
 * paid" and "Yes, record it" at once. The note field is the invoice reference
 * in practice, and it is optional: a director who does not have it to hand
 * should not be stopped from recording what they know.
 */
function RecordControl({
  confirming,
  busy,
  onAsk,
  onCancel,
  onRecord,
}: {
  confirming: boolean;
  busy: boolean;
  onAsk: () => void;
  onCancel: () => void;
  onRecord: (note: string) => void;
}) {
  const [note, setNote] = useState("");
  const noteId = useId();

  if (!confirming) {
    return (
      <button type="button" className="transactions__record" disabled={busy} onClick={onAsk}>
        {TRANSACTIONS_COPY.actions.record}
      </button>
    );
  }

  return (
    <form
      className="transactions__record-confirm"
      onSubmit={(event) => {
        event.preventDefault();
        onRecord(note);
      }}
    >
      <p className="transactions__record-note">{TRANSACTIONS_COPY.actions.recordNote}</p>
      <label className="transactions__record-label" htmlFor={noteId}>
        {TRANSACTIONS_COPY.actions.notePlaceholder}
      </label>
      <input
        id={noteId}
        className="transactions__record-input"
        value={note}
        onChange={(event) => setNote(event.target.value)}
        maxLength={2000}
        disabled={busy}
      />
      <div className="transactions__record-actions">
        <button type="submit" className="transactions__record-yes" disabled={busy}>
          {TRANSACTIONS_COPY.actions.confirm}
        </button>
        <button type="button" className="transactions__record-no" onClick={onCancel} disabled={busy}>
          {TRANSACTIONS_COPY.actions.cancel}
        </button>
      </div>
    </form>
  );
}
